import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = ['Python', 'Go', 'React', 'TypeScript', 'Kubernetes', 'Terraform', 'GCP', 'Redis'];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              Hello! I’m Gurudatta, an engineer who loves fusing AI models with resilient systems.
              I cut my teeth shipping production Golang and Python services, and these days I’m just
              as happy standing up Kubernetes clusters as I am crafting frontend polish in React.
            </p>

            <p>
              I’ve had the chance to lead initiatives across{' '}
              <a href="https://www.hp.com" target="_blank" rel="noreferrer">
                HP Inc.
              </a>{' '}
              (SONAR diagnostics, ML deployment pipelines),{' '}
              <a href="https://razorpay.com" target="_blank" rel="noreferrer">
                Razorpay
              </a>{' '}
              (100+ REST APIs powering thousands of entrepreneurs), the{' '}
              <a href="https://fragments.so" target="_blank" rel="noreferrer">
                Fragments DAO platform
              </a>
              , and{' '}
              <a href="https://www.tomtom.com" target="_blank" rel="noreferrer">
                TomTom
              </a>
              . That mix taught me how to balance developer velocity with rock-solid reliability.
            </p>

            <p>
              While pursuing my Master’s at UW–Madison, I built research tooling for Single Nucleus
              ATAC-Seq labs—containerized pipelines that uncover cancer cell anomalies and feed new
              hypotheses back to scientists. Academia sharpened my rigor; industry sharpened my bias
              for action.
            </p>

            <p>
              I’m currently focused on helping AI products feel responsive, safe, and delightful for
              the 100k+ real players who depend on them every week. Here are a few tools I reach for
              most often:
            </p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpeg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
